import { useContext, useEffect } from "react";
import { useImmer } from "use-immer";
import { useDebouncedCallback } from "use-debounce";
import { errorMessage } from "../../../utils";
import { getCustomerStampCards } from "../api";
import { AppContext } from "../../../store";
import qs from "qs";

export const useCustomerStampCards = ({ customerId }) => {
  const { appState } = useContext(AppContext);
  const { globalData } = appState;

  const [state, setState] = useImmer({
    isLoading: true,
    data: [],
    pagination: {
      pageIndex: 1,
      pageSize: 50,
      pageCount: 0,
      total: 0,
      hasMorePages: true,
      lastPage: 1,
    },
    sortBy: [],
    searchText: "",
    filters: {
      status: "",
    },
  });

  // Status options from global data
  const statusOptions =
    globalData?.stamp_cards?.customer_stamp_card_status || [];

  // Fetch customer stamp cards
  const fetchCustomerStampCards = async ({
    search = "",
    status = "",
    pagination,
    sortBy = [],
  }) => {
    if (!customerId) return;

    try {
      setState((draft) => {
        draft.isLoading = true;
      });

      let params = {};
      if (search) {
        params = { ...params, search };
      }
      if (status) {
        params = { ...params, status };
      }
      if (sortBy.length > 0) {
        const sortByParams = sortBy.reduce(
          (obj, item) =>
            Object.assign(obj, { [item.id]: item.desc ? "desc" : "asc" }),
          {},
        );
        params = {
          ...params,
          sort: sortByParams,
        };
      }

      const query = {
        params,
        paramsSerializer: (params) => qs.stringify(params),
      };

      const res = await getCustomerStampCards(query, pagination, customerId);

      if (res.success) {
        setState((draft) => {
          draft.data = res.data.stamp_cards || [];
          draft.pagination = res.data.pagination || draft.pagination;
          draft.isLoading = false;
        });
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message || "Failed to fetch stamp cards",
      );
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  // Debounced search
  const debouncedSearch = useDebouncedCallback((value) => {
    fetchCustomerStampCards({
      search: value,
      status: state.filters.status,
      pagination: {
        pageIndex: 1,
        pageSize: state.pagination.pageSize,
      },
      sortBy: state.sortBy,
    });
  }, 1000);

  // Initial load
  useEffect(() => {
    if (customerId) {
      fetchCustomerStampCards({
        search: "",
        status: "",
        pagination: {
          pageIndex: 1,
          pageSize: 50,
        },
        sortBy: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // Handle table search
  const handleTableSearch = (e) => {
    const value = e.target.value;
    setState((draft) => {
      draft.searchText = value;
    });
    debouncedSearch(value);
  };

  // Handle status filter
  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setState((draft) => {
      draft.filters.status = value;
    });

    fetchCustomerStampCards({
      search: state.searchText,
      status: value,
      pagination: {
        pageIndex: 1,
        pageSize: state.pagination.pageSize,
      },
      sortBy: state.sortBy,
    });
  };

  // Handle pagination
  const gotoPage = (page) => {
    fetchCustomerStampCards({
      search: state.searchText,
      status: state.filters.status,
      pagination: {
        pageIndex: page,
        pageSize: state.pagination.pageSize,
      },
      sortBy: state.sortBy,
    });
    setState((draft) => {
      draft.pagination.pageIndex = page;
    });
  };

  const setPageSize = (e) => {
    const pageSize = +e.target.value;
    fetchCustomerStampCards({
      search: state.searchText,
      status: state.filters.status,
      pagination: {
        pageIndex: 1,
        pageSize: pageSize,
      },
      sortBy: state.sortBy,
    });
    setState((draft) => {
      draft.pagination.pageSize = pageSize;
      draft.pagination.pageIndex = 1;
    });
  };

  const handleSort = (sortBy) => {
    fetchCustomerStampCards({
      search: state.searchText,
      status: state.filters.status,
      pagination: state.pagination,
      sortBy,
    });
    setState((draft) => {
      draft.sortBy = sortBy;
    });
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setState((draft) => {
      draft.filters.status = "";
    });
    fetchCustomerStampCards({
      search: state.searchText,
      status: "",
      pagination: {
        pageIndex: 1,
        pageSize: state.pagination.pageSize,
      },
      sortBy: state.sortBy,
    });
  };

  return {
    state,
    statusOptions,
    gotoPage,
    handleSort,
    setPageSize,
    handleTableSearch,
    handleStatusFilter,
    handleClearFilters,
  };
};
